"use client";

import React from "react";
import {
  Box,
  Flex,
  HStack,
  Button,
  useToast,
  Select,
  Tabs,
  TabList,
  Tab,
  Text,
  Spinner,
} from "@chakra-ui/react";
import { ShoppingCart, Phone } from "lucide-react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useDisclosure } from "@chakra-ui/react";
import WhatsAppAgents from "../WhatsAppAgents";
import WhatsAppKnowledgeBase from "./WhatsAppKnowledgeBase";
import { ConnectWhatsAppNumberDialog } from "../whatsapp-phone-numbers/ConnectWhatsAppNumberDialog";
import { useAuth } from "../../contexts/AuthContext";
import { useStableCredits } from "../../hooks/useStableCredits";

const MotionBox = motion(Box);

const WhatsAppPhoneNumbers = () => {
  const [selectedWhatsAppTab, setSelectedWhatsAppTab] =
    React.useState<string>("phone-numbers");
  const [selectedAgentType, setSelectedAgentType] = React.useState<string>(
    () => {
      return localStorage.getItem("currentAgentType") || "WhatsApp";
    }
  );

  const toast = useToast();
  const navigate = useNavigate();
  const { isAdmin, currentClientId, clients, userProfile } = useAuth();
  const credits = useStableCredits();

  const companyName = React.useMemo(() => {
    const currentClient = clients.find((c) => c.id === currentClientId);
    return isAdmin && currentClient ? currentClient.company_name : userProfile?.company_name;
  }, [isAdmin, currentClientId, clients, userProfile]);

  const canBuyNumbers = ["LeadiumX", "Collective Solar"].includes(companyName ?? "");

  const [isLoading, setIsLoading] = React.useState(true);

  const {
    isOpen: isConnectWhatsAppOpen,
    onOpen: onConnectWhatsAppOpen,
    onClose: onConnectWhatsAppClose,
  } = useDisclosure();

  React.useEffect(() => {
    // hydrate UI state from localStorage
    const savedTab = localStorage.getItem("currentAgentTab") || "phone-numbers";
    setSelectedWhatsAppTab(savedTab);

    const handleTabChange = (event: any) => {
      setSelectedWhatsAppTab(event.detail);
      localStorage.setItem("currentAgentTab", event.detail);
    };

    const handleAgentTypeChangeEvent = (event: any) => {
      setSelectedAgentType(event.detail);
    };

    window.addEventListener("agentTabChange", handleTabChange);
    window.addEventListener("agentTypeChange", handleAgentTypeChangeEvent);

    // ✅ loading done once hydration + listeners are set
    setIsLoading(false);

    return () => {
      window.removeEventListener("agentTabChange", handleTabChange);
      window.removeEventListener("agentTypeChange", handleAgentTypeChangeEvent);
    };
  }, []);

  const handleTabChange = (newTab: string) => {
    setSelectedWhatsAppTab(newTab);
    localStorage.setItem("currentAgentTab", newTab);
    window.dispatchEvent(new CustomEvent("agentTabChange", { detail: newTab }));
  };

  const handleBuyPhoneNumber = () => {
    toast({
      title: "Coming Soon",
      description: "WhatsApp phone number feature is under development.",
      status: "info",
      duration: 3000,
    });
  };

  const handleAgentTypeChange = (type: string) => {
    setSelectedAgentType(type);
    localStorage.setItem("currentAgentType", type);

    // ✅ optionally show spinner while routing away
    setIsLoading(true);

    if (type === "All") {
      navigate("/all-agents");
    } else if (type === "Voice") {
      navigate("/voice-agents");
    } else if (type === "SMS") {
      navigate("/sms-agents");
    } else if (type === "Email") {
      navigate("/email-agents");
    } else if (type === "WhatsApp") {
      navigate("/whatsapp-agents");
    } else {
      // "All" or anything else stays here, so stop loading
      setIsLoading(false);
    }
  };

  if (selectedAgentType === "WhatsApp") {
    if (selectedWhatsAppTab === "agents") {
      return <WhatsAppAgents />;
    }
    if (selectedWhatsAppTab === "knowledge-base") {
      return <WhatsAppKnowledgeBase />;
    }
  }

  const getTabIndex = () => {
    switch (selectedWhatsAppTab) {
      case "agents":
        return 0;
      case "phone-numbers":
        return 1;
      case "knowledge-base":
        return 2;
      default:
        return 1;
    }
  };

  const handleTabIndexChange = (index: number) => {
    const tabs = ["agents", "phone-numbers", "knowledge-base"];
    handleTabChange(tabs[index]);
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" h="100vh" w="100%">
        <Spinner size="xl" color="green.500" />
      </Flex>
    );
  }

  return (
    <Box p={6} bg="white" minH="100vh">
      <Flex justify="space-between" align="center" mb={6}>
        <HStack spacing={4}>
          <Tabs
            index={getTabIndex()}
            onChange={handleTabIndexChange}
            variant="solid-rounded"
            colorScheme="blackAlpha"
          >
            <TabList
              bg="gray.100"
              p={1}
              borderRadius="lg"
              border="1px solid"
              borderColor="gray.300"
            >
              <Tab
                fontWeight="bold"
                fontSize="md"
                color="black"
                _selected={{
                  bg: "white",
                  color: "black",
                  boxShadow: "sm",
                }}
                _hover={{
                  bg: selectedWhatsAppTab === "agents" ? "white" : "gray.200",
                }}
              >
                💬 WhatsApp Agents
              </Tab>
              <Tab
                fontWeight="bold"
                fontSize="md"
                color="black"
                _selected={{
                  bg: "white",
                  color: "black",
                  boxShadow: "sm",
                }}
                _hover={{
                  bg:
                    selectedWhatsAppTab === "phone-numbers"
                      ? "white"
                      : "gray.200",
                }}
              >
                📞 WhatsApp Phone Numbers
              </Tab>
              <Tab
                fontWeight="bold"
                fontSize="md"
                color="black"
                _selected={{
                  bg: "white",
                  color: "black",
                  boxShadow: "sm",
                }}
                _hover={{
                  bg:
                    selectedWhatsAppTab === "knowledge-base"
                      ? "white"
                      : "gray.200",
                }}
              >
                📚 Knowledge Base
              </Tab>
            </TabList>
          </Tabs>
        </HStack>

        <HStack spacing={2}>
          <MotionBox
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            transition="0.15s ease"
          >
            <Select
              value={selectedAgentType}
              onChange={(e) => handleAgentTypeChange(e.target.value)}
              height="48px"
              bg="white"
              border="2px solid"
              borderColor="gray.200"
              borderRadius="12px"
              color="gray.800"
              fontWeight="semibold"
              fontSize="sm"
              pl="12px"
              pr="36px"
              cursor="pointer"
              shadow="sm"
              transition="all 0.2s ease"
              _hover={{
                borderColor: "primary.400",
                shadow: "md",
              }}
              _focus={{
                borderColor: "primary.600",
                boxShadow: "0 0 0 3px rgba(99, 102, 241, 0.15)",
              }}
            >
              <option value="All">🤖 All Agents</option>
              <option value="Voice">📞 Voice Agents</option>
              <option value="SMS">💬 SMS Agents</option>

              {process.env.REACT_APP_IS_MAIN_APP === "true" && (
                <option value="Email">📧 Email Agents</option>
              )}

              <option value="WhatsApp">📱 WhatsApp Agents</option>
            </Select>
          </MotionBox>

          {canBuyNumbers && (
            <Button
            onClick={handleBuyPhoneNumber}
            variant="brandGradient"
            fontWeight="medium"
            leftIcon={<ShoppingCart size={16} />}
            isDisabled={credits !== null && credits < 0.01}
            title={
              credits !== null && credits < 0.01
                ? "Insufficient balance to Connect a WhatsApp number"
                : undefined
            }
          >
            Connect WhatsApp Number
          </Button>
          )}

          <Button
            leftIcon={<ShoppingCart size={16} />}
            onClick={onConnectWhatsAppOpen}
            variant="outline"
            size="md"
            colorScheme="blue"
            fontWeight="medium"
          >
            Connect Your Number
          </Button>
        </HStack>
      </Flex>

      <Flex
        direction="column"
        align="center"
        justify="center"
        h="500px"
        minH="calc(100vh - 80px)"
        bg="gray.50"
        borderRadius="md"
        border="1px solid"
        borderColor="gray.200"
      >
        <Phone size={48} color="gray" />
        <Text fontSize="lg" fontWeight="medium" color="gray.600" mt={4}>
          No WhatsApp Phone Numbers Found!
        </Text>
        <Text fontSize="sm" color="gray.500" mt={2}>
          Connect a WhatsApp phone number to get started.
        </Text>

        <Box mt={6}>
          <Button
            onClick={handleBuyPhoneNumber}
            mt={6}
            colorScheme="primary"
            bg="primary.800"
            _hover={{ bg: "primary.900" }}
            leftIcon={<ShoppingCart size={16} />}
            isDisabled={credits !== null && credits < 0.01}
            title={
              credits !== null && credits < 0.01
                ? "Insufficient balance to Connect a WhatsApp number"
                : undefined
            }
          >
            Connect WhatsApp Number
          </Button>
        </Box>
      </Flex>

      <ConnectWhatsAppNumberDialog
        isOpen={isConnectWhatsAppOpen}
        onClose={onConnectWhatsAppClose}
        onSuccess={() => {}}
      />
    </Box>
  );
};

export default WhatsAppPhoneNumbers;
